import React, { useState } from 'react';
import { useAdmin } from '../hooks/useAdmin';
import MaintenanceControl from './admin/MaintenanceControl';
import SystemMonitor from './admin/SystemMonitor';
import GameManagement from './admin/GameManagement';
import RewardsManagement from './admin/RewardsManagement';
import WelcomeAirdropManager from './admin/WelcomeAirdropManager';
import NotificationManagement from './admin/NotificationManagement';
import styles from '../styles/AdminPanel.module.css';

type AdminTab = 'maintenance' | 'system' | 'games' | 'rewards' | 'airdrops' | 'notifications';

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('maintenance');
  const { user: adminUser, isAdmin, logout } = useAdmin();

  const tabs = [
    { id: 'maintenance' as AdminTab, label: 'Maintenance', icon: '🔧' },
    { id: 'games' as AdminTab, label: 'Management', icon: '🎮' },
    { id: 'rewards' as AdminTab, label: 'Rewards', icon: '🎁' },
    { id: 'airdrops' as AdminTab, label: 'Airdrops', icon: '🪂' },
    { id: 'notifications' as AdminTab, label: 'Notifications', icon: '🔔' },
    { id: 'system' as AdminTab, label: 'System Monitor', icon: '📊' },
  ];

  const handleLogout = async () => {
    const confirmed = window.confirm('Are you sure you want to logout from admin panel?');
    if (confirmed) {
      logout();
    }
  };

  if (!isAdmin) {
    return (
      <div className={`${styles.adminPanel} ${styles.adminAccessDenied}`}>
        <div className={styles.accessDeniedContainer}>
          <h1>⚠️ Access Denied</h1>
          <p>You do not have administrator privileges.</p>
          <button onClick={() => window.location.href = '/'} className={`${styles.btn} ${styles.btnPrimary}`}>
            Return to Game
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminPanel}>
      {/* Admin Header */}
      <div className={styles.adminHeader}>
        <div className={styles.adminHeaderContent}>
          <div className={styles.adminTitle}>
            <h1>🛠️ Admin Panel</h1>
            <span className={styles.adminSubtitle}>DeathX Fun Administration</span>
          </div>
          
          <div className={styles.adminUserInfo}>
            <div className={styles.adminUserDetails}>
              <span className={styles.adminUserLabel}>Admin:</span>
              <span className={styles.adminUserAddress}>
                {adminUser?.walletAddress ? 
                  `${adminUser.walletAddress.slice(0, 6)}...${adminUser.walletAddress.slice(-4)}` : 
                  'Unknown'
                }
              </span>
            </div>
            <button onClick={handleLogout} className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}>
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={styles.adminNavigation}>
        <div className={styles.adminTabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className={styles.adminContent}>
        <div className={styles.adminContentWrapper}>
          {activeTab === 'maintenance' && (
            <div className={styles.adminTabContent}>
              <MaintenanceControl />
            </div>
          )}
          
          {activeTab === 'games' && (
            <div className={styles.adminTabContent}>
              <GameManagement />
            </div>
          )}
          
          {activeTab === 'rewards' && (
            <div className={styles.adminTabContent}>
              <RewardsManagement />
            </div>
          )}
          
          {activeTab === 'airdrops' && (
            <div className={styles.adminTabContent}>
              <WelcomeAirdropManager />
            </div>
          )}
          
          {activeTab === 'notifications' && (
            <div className={styles.adminTabContent}>
              <NotificationManagement />
            </div>
          )}
          
          {activeTab === 'system' && (
            <div className={styles.adminTabContent}>
              <SystemMonitor />
            </div>
          )}
        </div>
      </div>

      {/* Admin Footer */}
      <div className={styles.adminFooter}>
        <div className={styles.adminFooterContent}>
          <div className={styles.footerLinks}>
            <a href="/" className={styles.footerLink}>🏠 Return to Game</a>
            <span className={styles.footerSeparator}>•</span>
            <span className={styles.footerInfo}>DeathX Fun Admin Panel v1.0</span>
          </div>
          
          <div className={styles.footerStatus}>
            <div className={styles.statusIndicator}>
              <div className={`${styles.statusDot} ${styles.online}`}></div>
              <span>Admin Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Actions (Hidden by default, can be shown on double-click) */}
      <div className={styles.adminEmergency} style={{ display: 'none' }}>
        <h3>🚨 Emergency Actions</h3>
        <p>Emergency controls for critical situations</p>
        <button className={`${styles.btn} ${styles.btnDanger}`}>
          🔴 Emergency Shutdown
        </button>
      </div>
    </div>
  );
};

export default AdminPanel;